/**
 * Централизованный сервис логирования
 * 
 * Предоставляет структурированное логирование с уровнями важности
 * и возможностью интеграции с внешними сервисами мониторинга (Sentry, etc)
 */

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4,
};

// Текущий уровень логирования (можно менять через env переменные)
const CURRENT_LEVEL = process.env.NODE_ENV === 'production' 
  ? LOG_LEVELS.WARN 
  : LOG_LEVELS.DEBUG;

class Logger {
  constructor() {
    this.enabled = true;
    this.level = CURRENT_LEVEL;
    this.logs = []; // Для хранения логов (опционально)
    this.maxLogs = 100; // Максимум логов в памяти
  }

  /**
   * Проверка, нужно ли логировать на данном уровне
   */
  shouldLog(level) {
    return this.enabled && level >= this.level;
  }

  /**
   * Добавить лог в историю
   */
  addToHistory(level, message, data) {
    if (this.logs.length >= this.maxLogs) {
      this.logs.shift(); // Удаляем старый лог
    }
    
    this.logs.push({
      timestamp: new Date().toISOString(),
      level,
      message,
      data,
    });
  }

  /**
   * Получить историю логов
   */
  getHistory() {
    return [...this.logs];
  }

  /**
   * Очистить историю логов
   */
  clearHistory() {
    this.logs = [];
  }

  /**
   * Debug уровень - детальная информация для разработки
   */
  debug(message, data = null) {
    if (!this.shouldLog(LOG_LEVELS.DEBUG)) return;

    if (data) {
      console.debug(`[DEBUG] ${message}`, data);
    } else {
      console.debug(`[DEBUG] ${message}`);
    }

    this.addToHistory(LOG_LEVELS.DEBUG, message, data);
  }

  /**
   * Info уровень - общая информация
   */
  info(message, data = null) {
    if (!this.shouldLog(LOG_LEVELS.INFO)) return;

    if (data) {
      console.info(`[INFO] ${message}`, data);
    } else {
      console.info(`[INFO] ${message}`);
    }

    this.addToHistory(LOG_LEVELS.INFO, message, data);
  }

  /**
   * Warn уровень - предупреждения
   */
  warn(message, data = null) {
    if (!this.shouldLog(LOG_LEVELS.WARN)) return;

    if (data) {
      console.warn(`[WARN] ${message}`, data);
    } else {
      console.warn(`[WARN] ${message}`);
    }

    this.addToHistory(LOG_LEVELS.WARN, message, data);

    // В будущем можно отправлять в Sentry как warning
    // this.sendToSentry(message, data, 'warning');
  }

  /**
   * Error уровень - ошибки
   */
  error(message, error = null, context = null) {
    if (!this.shouldLog(LOG_LEVELS.ERROR)) return;

    const errorData = {
      message: error?.message || message,
      stack: error?.stack,
      name: error?.name,
      context,
    };

    console.error(`[ERROR] ${message}`, errorData);

    // Убираем stack из истории (может быть очень большим)
    const historyData = {
      ...errorData,
      stack: error?.stack ? '[Stack trace available]' : undefined,
    };
    this.addToHistory(LOG_LEVELS.ERROR, message, historyData);

    // В будущем можно отправлять в Sentry
    // this.sendToSentry(message, errorData, 'error');
  }

  /**
   * Логирование API ошибок с контекстом
   */
  apiError(url, method, status, error, responseData = null) {
    const message = `API Error: ${method} ${url} - ${status}`;
    const context = {
      url,
      method,
      status,
      responseData,
      timestamp: new Date().toISOString(),
    };

    this.error(message, error, context);
  }

  /**
   * Логирование сетевых ошибок
   */
  networkError(url, method, error) {
    const message = `Network Error: ${method} ${url}`;
    const context = {
      url,
      method,
      errorType: error.code || 'UNKNOWN',
      message: error.message,
    };

    this.error(message, error, context);
  }

  /**
   * Группированное логирование (для связанных логов)
   */
  group(label, callback) {
    if (this.shouldLog(LOG_LEVELS.DEBUG)) {
      console.group(label);
      callback();
      console.groupEnd();
    }
  }

  /**
   * Логирование производительности
   */
  performance(label, duration) {
    if (this.shouldLog(LOG_LEVELS.DEBUG)) {
      console.log(`⏱️ [PERF] ${label}: ${duration}ms`);
    }
  }

  /**
   * Отключить/включить логирование
   */
  setEnabled(enabled) {
    this.enabled = enabled;
  }

  /**
   * Установить уровень логирования
   */
  setLevel(level) {
    this.level = level;
  }
}

// Создаем единственный экземпляр logger
export const logger = new Logger();

// Экспортируем по умолчанию для удобства
export default logger;

